import React, { useState } from "react";
import api from "../services/apiService"; // Axios-Instanz
import { Helmet } from "react-helmet";  // Import von React Helmet

const FibuExportList = () => {
  // Aktuelles Jahr als String
  const currentYear = new Date().getFullYear().toString();

  // Initialisiere die States für das Jahr (CreditNotes & Invoices) mit dem aktuellen Jahr
  const [creditNoteYear, setCreditNoteYear] = useState(currentYear);
  const [invoiceYear, setInvoiceYear] = useState(currentYear);

  // Erzeuge eine Liste der letzten 10 Jahre (inkl. aktuelles Jahr)
  const years = [];
  for (let i = new Date().getFullYear(); i >= new Date().getFullYear() - 10; i--) {
    years.push(i.toString());
  }

  // Optional: Zur Anzeige (Debug) der abgerufenen JSON-Daten
  const [creditNotesData, setCreditNotesData] = useState(null);
  const [invoicesData, setInvoicesData] = useState(null);
  const [invoiceExportError, setInvoiceExportError] = useState(null);
  const [exportingInvoices, setExportingInvoices] = useState(false);
  const [customersData, setCustomersData] = useState(null);

  // Helper: Konvertiert ein JSON-Array in einen CSV-String (ohne zusätzliche Anführungszeichen)
const convertToCSV = (data) => {
    if (!data || data.length === 0) return "";
    const headers = Object.keys(data[0]);
    const csvRows = [];
    // Kopfzeile (Semikolon als Separator)
    csvRows.push(headers.join(";"));
    data.forEach(row => {
      const values = headers.map(header => {
        const val = row[header];
        // Falls der Wert null oder undefined ist, als leeren String ausgeben
        const text = val !== null && val !== undefined ? String(val) : "";
        return /[;"\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
      });
      csvRows.push(values.join(";"));
    });
    return csvRows.join("\n");
  };

  // Helper: Löst den CSV-Download aus
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handler für den CreditNotes-Export (Gutschriften)
  const handleCreditNotesExport = async () => {
    if (!creditNoteYear) {
      alert("Bitte ein Jahr für den Gutschrifts-Export angeben.");
      return;
    }
    try {
      // Beispielendpunkt: /fibucn/{year}
      const response = await api.get(`/fibucn/${creditNoteYear}`);
      const data = response.data.data;  // Annahme: API liefert das Array in data.data
      setCreditNotesData(data);         // Optional zum Debuggen
      const csvContent = convertToCSV(data);
      // Hier wird der Dateiname als GS_YYYY.csv gesetzt
      const filename = `GS_${creditNoteYear}.csv`;
      downloadCSV(csvContent, filename);
    } catch (error) {
      console.error("Fehler beim Abrufen der Gutschriften:", error);
      alert("Fehler beim Abrufen der Gutschriften.");
    }
  };

  // Handler für den Invoice-Export (Rechnungen)
  const handleInvoicesExport = async () => {
    if (!invoiceYear) {
      alert("Bitte ein Jahr für den Rechnungs-Export angeben.");
      return;
    }
    setExportingInvoices(true);
    setInvoiceExportError(null);
    setInvoicesData(null);
    try {
      const response = await api.post(`/fibuin/${invoiceYear}`);
      const data = response.data.data;  // Annahme: API liefert das Array in data.data
      setInvoicesData(data);            // Optional zum Debuggen
      const csvContent = convertToCSV(data);
      // Hier wird der Dateiname als RE_YYYY.csv gesetzt
      const filename = `RE_${invoiceYear}.csv`;
      downloadCSV(csvContent, filename);
    } catch (error) {
      console.error("Fehler beim Abrufen der Rechnungen:", error);
      setInvoiceExportError(error?.response?.data || { error: "Fehler beim Abrufen der Rechnungen." });
    } finally {
      setExportingInvoices(false);
    }
  };

  // Handler für den Kunden-Export
const handleCustomersExport = async () => {
  try {
    // Beispielendpunkt: /fibucu (achte darauf, dass dieser Endpunkt ein Objekt mit { message, data } liefert)
    const response = await api.get(`/fibucu`);
    // Extrahiere das Array von Kundenobjekten aus response.data.data
    const data = response.data.data;
    setCustomersData(data);          // Optional zum Debuggen
    const csvContent = convertToCSV(data);
    // Hier wird der Dateiname als KD_YYYY.csv gesetzt; wir verwenden das aktuelle Jahr
    const filename = `KD_${currentYear}.csv`;
    downloadCSV(csvContent, filename);
  } catch (error) {
    console.error("Fehler beim Abrufen der Kunden:", error);
    alert("Fehler beim Abrufen der Kunden.");
  }
};

  return (
    <div style={{ maxWidth: "600px", margin: "auto" }}>
      <Helmet>
        <title>Fibu Export</title>
        <meta name="description" content="Fibu Export" />
      </Helmet>
      <h2>Fibu Export</h2>

      {/* Abschnitt Gutschriften */}
      <div style={{ border: "1px solid #ccc", padding: "1rem", marginBottom: "1rem" }}>
        <h3>Gutschriften Export</h3>
        <div>
          <label htmlFor="creditNoteYear">Jahr: </label>
          <select
            id="creditNoteYear"
            value={creditNoteYear}
            onChange={(e) => setCreditNoteYear(e.target.value)}
            style={{ width: "120px" }}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <button onClick={handleCreditNotesExport}>Export Gutschriften</button>
        </div>
        {creditNotesData && (
          <div>
            <h4>Gutschriften Daten (JSON, Debug):</h4>
            <pre>{JSON.stringify(creditNotesData, null, 2)}</pre>
          </div>
        )}
      </div>

      <p>Für Auslandsrechnungen werden die in der Buchungskontenverwaltung zugeordneten Auslandskonten verwendet.
        Die Steuerschlüssel werden im FiBu-Programm gepflegt. Das CSV-Format bleibt gleich.</p>
      <p>Beim ersten erfolgreichen Rechnungsexport werden die Buchungsdaten gespeichert.
        Wiederholte Exporte verwenden diese Daten; neu hinzugekommene Rechnungen werden ergänzt.</p>
      {/* Abschnitt Rechnungen */}
      <div style={{ border: "1px solid #ccc", padding: "1rem", marginBottom: "1rem" }}>
        <h3>Rechnungen Export</h3>
        <div>
          <label htmlFor="invoiceYear">Jahr: </label>
          <select
            id="invoiceYear"
            disabled={exportingInvoices}
            value={invoiceYear}
            onChange={(e) => setInvoiceYear(e.target.value)}
            style={{ width: "120px" }}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <button onClick={handleInvoicesExport} disabled={exportingInvoices}>
            {exportingInvoices ? 'Export wird erstellt…' : 'Export Rechnungen'}
          </button>
        </div>
        {invoiceExportError && (
          <div role="alert">
            <p>{invoiceExportError.error || 'Der Export konnte nicht erstellt werden.'}</p>
            {invoiceExportError.issues?.length > 0 && (
              <ul>{invoiceExportError.issues.map((issue, index) => (
                <li key={index}>
                  Rechnung {issue.invoiceNumber} · Konto {issue.sourceAccount || 'fehlt'} · {issue.taxTreatmentLabel}:
                  {' '}{issue.message}{issue.itemName ? ` (Artikel: ${issue.itemName})` : ''}
                </li>
              ))}</ul>
            )}
          </div>
        )}
        {invoicesData && (
          <div>
            <h4>Rechnungs Daten (JSON, Debug):</h4>
            <pre>{JSON.stringify(invoicesData, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Abschnitt Kunden */}
      <div style={{ border: "1px solid #ccc", padding: "1rem" }}>
        <h3>Kunden Export</h3>
        <div>
          <button onClick={handleCustomersExport}>Export Kunden</button>
        </div>
        {customersData && (
          <div>
            <h4>Kunden Daten (JSON, Debug):</h4>
            <pre>{JSON.stringify(customersData, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default FibuExportList;
